<template>
  <div class="fixed inset-0 z-[60] bg-black/50 overflow-y-auto py-6 px-4">
    <div class="bg-white rounded-2xl w-full max-w-4xl mx-auto shadow-xl">
      <!-- 头部 -->
      <div
        class="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-neutral-100 px-6 py-4 rounded-t-2xl flex items-start justify-between gap-4"
      >
        <div class="min-w-0">
          <h2 class="text-lg font-bold text-neutral-900">
            <i class="fa fa-graduation-cap text-primary mr-1.5"></i>教学诊断
          </h2>
          <p class="text-xs text-neutral-500 mt-1 truncate">
            {{ link.title }}
            <span class="text-neutral-300">·</span>
            {{ link.subject }}
            <span v-if="link.stage" class="text-neutral-300">·</span>
            {{ stageText(link.stage) }}
          </p>
        </div>
        <button class="text-neutral-400 hover:text-neutral-600 mt-1" @click="$emit('close')">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="p-6 space-y-5">
        <!-- 学情概览 -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <p class="text-2xl font-bold text-neutral-900">{{ stats.wrongCount || 0 }}</p>
            <p class="text-xs text-neutral-400 mt-0.5">命中错题</p>
          </div>
          <div class="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <p class="text-2xl font-bold text-neutral-900">{{ stats.relatedCount || 0 }}</p>
            <p class="text-xs text-neutral-400 mt-0.5">可关联知识点</p>
          </div>
          <div class="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <p class="text-2xl font-bold text-neutral-900">{{ records.length }}</p>
            <p class="text-xs text-neutral-400 mt-0.5">教案 / 复盘</p>
          </div>
          <div class="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <p class="text-2xl font-bold" :class="scoreTextClass(latestScore)">
              {{ latestScore === null ? "—" : latestScore }}
            </p>
            <p class="text-xs text-neutral-400 mt-0.5">最近诊断评分</p>
          </div>
        </div>

        <!-- 可关联内容 + 典型错题 -->
        <div v-if="relatedList.length || wrongSamples.length" class="grid md:grid-cols-2 gap-3">
          <div v-if="relatedList.length" class="rounded-xl border border-sky-100 bg-sky-50/60 p-4">
            <p class="text-xs font-medium text-sky-700 mb-2">
              <i class="fa fa-link mr-1"></i>已关联的知识点（可作为讲解切入点）
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="r in relatedList"
                :key="r.id"
                class="text-xs px-2 py-1 rounded bg-white border border-sky-100 text-sky-700"
              >
                {{ r.title }}
              </span>
            </div>
          </div>
          <div v-if="wrongSamples.length" class="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
            <p class="text-xs font-medium text-amber-700 mb-2">
              <i class="fa fa-exclamation-triangle mr-1"></i>学生相关错题（诊断会自动带入）
            </p>
            <ul class="space-y-1">
              <li v-for="(s, i) in wrongSamples" :key="i" class="text-xs text-amber-800 truncate">
                · {{ s }}
              </li>
            </ul>
          </div>
        </div>

        <!-- 新建 / 编辑表单 -->
        <div v-if="formVisible" class="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-4">
          <div class="grid sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">课题 *</label>
              <input
                v-model="form.title"
                maxlength="200"
                placeholder="如：分数乘法 第 1 课时"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">年级 / 班级</label>
              <input
                v-model="form.grade"
                maxlength="50"
                placeholder="如：五年级 3 班"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              教案（目标、环节、例题与提问设计）
            </label>
            <textarea
              v-model="form.planContent"
              rows="6"
              placeholder="本节课准备怎么讲：导入用什么情境、新授分几步、每步配什么例子、练习怎么分层…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary resize-y"
            ></textarea>
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              上课分析（实际怎么讲、学生反应、卡在哪里）
            </label>
            <textarea
              v-model="form.classAnalysis"
              rows="5"
              placeholder="实际上课时哪些环节顺利完成、哪些地方学生卡住了、临时做了哪些调整…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary resize-y"
            ></textarea>
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              学生做题情况（正确率、分层表现、典型错误）
            </label>
            <textarea
              v-model="form.studentData"
              rows="4"
              placeholder="如：课堂练习 20 人全对 12 人，典型错误是把…；课后作业正确率 62%…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary resize-y"
            ></textarea>
            <p class="text-[11px] text-neutral-400 mt-1">
              已自动统计该知识点的错题 {{ stats.wrongCount || 0 }} 道，诊断时会一并交给 AI 分析。
            </p>
          </div>
          <div class="flex justify-end gap-2">
            <button
              class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600 bg-white"
              @click="cancelForm"
            >
              取消
            </button>
            <button
              class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? "保存中..." : "保存" }}
            </button>
          </div>
        </div>

        <!-- 记录列表 -->
        <div v-if="loading" class="py-12 text-center text-neutral-400">
          <i class="fa fa-circle-o-notch fa-spin text-xl"></i>
        </div>

        <div v-else-if="!records.length && !formVisible" class="text-center py-12">
          <i class="fa fa-file-text-o text-3xl text-neutral-300 mb-3"></i>
          <p class="text-sm text-neutral-500">还没有教案与上课分析</p>
          <p class="text-xs text-neutral-400 mt-1">
            填入教案和课堂情况后，AI 会判断讲解是否合适、还能结合哪些知识点讲、学生是否真的掌握
          </p>
          <button
            class="mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm"
            @click="newRecord"
          >
            <i class="fa fa-plus mr-1"></i>新建第一条
          </button>
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="r in records"
            :key="r.id"
            class="rounded-xl border border-neutral-200 overflow-hidden"
          >
            <!-- 卡片头 -->
            <div class="flex items-start gap-3 p-4 hover:bg-neutral-50">
              <button class="text-neutral-400 mt-0.5" @click="toggle(r.id)">
                <i class="fa text-xs" :class="expanded[r.id] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </button>
              <div class="flex-1 min-w-0 cursor-pointer" @click="toggle(r.id)">
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="font-bold text-neutral-900 truncate">{{ r.title }}</p>
                  <span
                    v-if="r.aiScore !== null && r.aiScore !== undefined"
                    class="text-[11px] px-2 py-0.5 rounded-full border"
                    :class="scoreBadgeClass(r.aiScore)"
                  >
                    诊断 {{ r.aiScore }} 分
                  </span>
                </div>
                <p class="text-xs text-neutral-400 mt-0.5">
                  <span v-if="r.grade">{{ r.grade }} · </span>
                  {{ formatTime(r.updateTime) }}
                </p>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <button
                  class="px-2.5 py-1 rounded-lg text-xs border border-primary/30 text-primary hover:bg-primary/5 disabled:opacity-50"
                  :disabled="analyzingId === r.id || !canAnalyze(r)"
                  @click="analyze(r)"
                >
                  <i class="fa mr-1" :class="analyzingId === r.id ? 'fa-circle-o-notch fa-spin' : 'fa-magic'"></i>
                  {{ analyzingId === r.id ? "诊断中" : "AI 诊断" }}
                </button>
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary"
                  title="编辑"
                  @click="edit(r)"
                >
                  <i class="fa fa-pencil"></i>
                </button>
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-400 hover:text-red-500"
                  title="删除"
                  @click="remove(r)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </div>

            <!-- AI 诊断结果 -->
            <div v-if="r.aiResult" class="border-t border-neutral-100 bg-neutral-50/60 p-4 space-y-4">
              <div class="flex items-start gap-3">
                <div
                  class="w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 border"
                  :class="scoreBadgeClass(r.aiResult.score)"
                >
                  <span class="text-lg font-bold leading-none">{{ r.aiResult.score }}</span>
                  <span class="text-[10px] mt-0.5">评分</span>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-medium text-neutral-900">{{ r.aiResult.verdict }}</p>
                  <p v-if="r.aiTime" class="text-[11px] text-neutral-400 mt-1">
                    诊断于 {{ formatTime(r.aiTime) }}
                  </p>
                </div>
              </div>

              <!-- 优点 -->
              <div v-if="(r.aiResult.strengths || []).length">
                <p class="text-xs font-medium text-emerald-700 mb-1.5">
                  <i class="fa fa-check-circle mr-1"></i>做得好的地方
                </p>
                <ul class="space-y-1">
                  <li v-for="(s, i) in r.aiResult.strengths" :key="i" class="text-xs text-neutral-700">
                    · {{ s }}
                  </li>
                </ul>
              </div>

              <!-- 问题与改法 -->
              <div v-if="(r.aiResult.issues || []).length">
                <p class="text-xs font-medium text-rose-600 mb-1.5">
                  <i class="fa fa-exclamation-circle mr-1"></i>问题与改法
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(it, i) in r.aiResult.issues"
                    :key="i"
                    class="bg-white rounded-lg border border-neutral-200 p-3"
                  >
                    <p class="text-xs text-neutral-800">
                      <span class="px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 mr-1.5">{{ it.stage }}</span>
                      {{ it.problem }}
                    </p>
                    <p class="text-xs text-emerald-700 mt-1.5">
                      <i class="fa fa-wrench mr-1"></i>{{ it.fix }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- 可结合的讲法 -->
              <div v-if="(r.aiResult.relatedPoints || []).length">
                <p class="text-xs font-medium text-indigo-600 mb-1.5">
                  <i class="fa fa-share-alt mr-1"></i>可结合这些内容来讲
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(rp, i) in r.aiResult.relatedPoints"
                    :key="i"
                    class="bg-white rounded-lg border border-indigo-100 p-3"
                  >
                    <p class="text-xs font-medium text-neutral-900">{{ rp.title }}</p>
                    <p class="text-xs text-neutral-500 mt-1">{{ rp.why }}</p>
                    <p class="text-xs text-indigo-700 mt-1">
                      <i class="fa fa-lightbulb-o mr-1"></i>{{ rp.how }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- 效果判断 -->
              <div v-if="r.aiResult.effect" class="bg-white rounded-lg border border-neutral-200 p-3 space-y-1.5">
                <p class="text-xs font-medium text-neutral-700">
                  <i class="fa fa-line-chart mr-1"></i>学习效果判断
                </p>
                <p class="text-xs text-neutral-700">{{ r.aiResult.effect.mastery }}</p>
                <p class="text-xs text-neutral-500">
                  <span class="text-neutral-400">依据：</span>{{ r.aiResult.effect.evidence }}
                </p>
                <p v-if="r.aiResult.effect.gap" class="text-xs text-rose-600">
                  <span class="text-neutral-400">仍需解决：</span>{{ r.aiResult.effect.gap }}
                </p>
              </div>

              <!-- 下一步动作 -->
              <div v-if="(r.aiResult.actions || []).length">
                <p class="text-xs font-medium text-neutral-700 mb-1.5">
                  <i class="fa fa-flag-o mr-1"></i>下节课直接可做
                </p>
                <ol class="space-y-1 list-decimal list-inside">
                  <li v-for="(a, i) in r.aiResult.actions" :key="i" class="text-xs text-neutral-700">
                    {{ a }}
                  </li>
                </ol>
              </div>
            </div>

            <!-- 展开的原始材料 -->
            <div v-if="expanded[r.id]" class="border-t border-neutral-100 p-4 space-y-3">
              <div>
                <p class="text-xs font-medium text-neutral-500 mb-1">教案</p>
                <p class="text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed">
                  {{ r.planContent || "未填写" }}
                </p>
              </div>
              <div>
                <p class="text-xs font-medium text-neutral-500 mb-1">上课分析</p>
                <p class="text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed">
                  {{ r.classAnalysis || "未填写" }}
                </p>
              </div>
              <div>
                <p class="text-xs font-medium text-neutral-500 mb-1">学生做题情况</p>
                <p class="text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed">
                  {{ r.studentData || "未填写" }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <p class="text-[11px] text-neutral-400">
          诊断只针对你填写的教案与课堂情况，并结合本账号错题本中相关题目；数据不足时 AI 会明确说明，不会替学生下结论。
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "LessonPanel",
  props: {
    link: { type: Object, required: true },
  },
  emits: ["close", "score"],
  data() {
    return {
      loading: false,
      saving: false,
      analyzingId: "",
      records: [],
      stats: {},
      expanded: {},
      formVisible: false,
      form: this.emptyForm(),
    };
  },
  computed: {
    relatedList() {
      return this.stats.related || [];
    },
    wrongSamples() {
      return this.stats.wrongSamples || [];
    },
    latestScore() {
      for (const r of this.records) {
        if (r.aiScore !== null && r.aiScore !== undefined) return r.aiScore;
      }
      return null;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        title: "",
        grade: "",
        planContent: "",
        classAnalysis: "",
        studentData: "",
      };
    },
    stageText(stage) {
      const map = { primary: "小学", junior: "初中", senior: "高中" };
      return map[stage] || stage || "";
    },
    formatTime(t) {
      if (!t) return "";
      const s = String(t).replace("T", " ");
      return s.length > 16 ? s.slice(0, 16) : s;
    },
    scoreTextClass(score) {
      if (score === null || score === undefined) return "text-neutral-400";
      if (score >= 80) return "text-emerald-600";
      if (score >= 60) return "text-amber-600";
      return "text-rose-600";
    },
    scoreBadgeClass(score) {
      const s = Number(score) || 0;
      if (s >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
      if (s >= 60) return "bg-amber-50 text-amber-700 border-amber-200";
      return "bg-rose-50 text-rose-700 border-rose-200";
    },
    canAnalyze(r) {
      return !!(r.planContent || r.classAnalysis);
    },
    toggle(id) {
      this.expanded[id] = !this.expanded[id];
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "lesson/list",
          method: "GET",
          params: { linkId: this.link.id },
        });
        if (res.code === 200 && res.data) {
          this.records = res.data.list || [];
          this.stats = res.data.stats || {};
          if (this.records.length) {
            this.expanded[this.records[0].id] = true;
          }
          this.emitScore();
        } else {
          ElMessage.error(res.msg || "加载教学记录失败");
        }
      } catch (e) {
        ElMessage.error("加载教学记录失败，请稍后重试");
      }
      this.loading = false;
    },
    emitScore() {
      const scored = this.records.filter(
        (r) => r.aiScore !== null && r.aiScore !== undefined
      );
      this.$emit("score", scored.length ? scored[0].aiScore : null);
    },
    newRecord() {
      this.form = this.emptyForm();
      this.form.title = `${this.link.title || ""} 教学记录`;
      this.formVisible = true;
    },
    edit(r) {
      this.form = {
        id: r.id,
        title: r.title || "",
        grade: r.grade || "",
        planContent: r.planContent || "",
        classAnalysis: r.classAnalysis || "",
        studentData: r.studentData || "",
      };
      this.formVisible = true;
      this.expanded[r.id] = true;
    },
    cancelForm() {
      this.formVisible = false;
      this.form = this.emptyForm();
    },
    async save() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写课题");
        return;
      }
      if (!this.form.planContent.trim() && !this.form.classAnalysis.trim()) {
        ElMessage.warning("请至少填写教案或上课分析");
        return;
      }
      this.saving = true;
      try {
        const payload = {
          id: this.form.id || null,
          linkId: this.link.id,
          title: this.form.title,
          grade: this.form.grade,
          planContent: this.form.planContent,
          classAnalysis: this.form.classAnalysis,
          studentData: this.form.studentData,
        };
        const res = await this.$axios({
          url: "lesson/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200) {
          ElMessage.success("已保存");
          this.formVisible = false;
          this.form = this.emptyForm();
          await this.load();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      }
      this.saving = false;
    },
    async remove(r) {
      try {
        await ElMessageBox.confirm(`确定删除「${r.title}」？`, "删除", {
          confirmButtonText: "删除",
          cancelButtonText: "取消",
          type: "warning",
        });
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "lesson/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: r.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          await this.load();
        } else {
          ElMessage.error(res.msg || "删除失败");
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },
    async analyze(r) {
      if (!this.canAnalyze(r)) {
        ElMessage.warning("请先填写教案或上课分析");
        return;
      }
      this.analyzingId = r.id;
      try {
        const res = await this.$axios({
          url: "lesson/analyze",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: r.id }) },
        });
        if (res.code === 200 && res.data) {
          const idx = this.records.findIndex((x) => x.id === r.id);
          if (idx >= 0) this.records[idx] = res.data;
          if (res.data.stats) this.stats = res.data.stats;
          this.expanded[r.id] = true;
          ElMessage.success("诊断完成");
          this.emitScore();
        } else {
          ElMessage.error(res.msg || "诊断失败");
        }
      } catch (e) {
        ElMessage.error("诊断失败，请稍后重试");
      }
      this.analyzingId = "";
    },
  },
};
</script>
